import React, { useRef, useEffect, useState } from 'react';
import ReactLoader from 'react-loading';

import { withTheme, ThemeProps } from 'styled-components';

import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { City } from 'models/city';
import { CitiesState } from 'reducers/cities';

import { Header, Result, RecentSearches } from './styles';

interface Props extends CitiesState, ThemeProps<any> {
  show: boolean;
  placeHolder: string;
  minLengthForSearch: number;
  onClose: () => void;
  onSearchChange: (value: string) => void;
  onChange: (city?: City) => void;
}

const CitiesSearch = ({
  show,
  placeHolder,
  bySearch,
  isLoading,
  theme,
  minLengthForSearch,
  onClose,
  onChange,
  onSearchChange,
}: Props) => {
  const { t } = useStoreTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  useEffect(() => {
    searchInputRef.current?.focus();
    setSearchQuery('');
  }, [show]);

  useEffect(() => {
    setIsLocalLoading(isLoading);
  }, [isLoading]);

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value && value.length > 2) {
      setIsLocalLoading(true);
    }

    setSearchQuery(value);
    onSearchChange(value);
  };

  const handleClickResultItem = (city: City) => {
    onChange(city);
    onClose();
  };

  const renderLoading = () => (
    <Result.Loading>
      <ReactLoader type="spin" color={theme.loading} width={60} height={60} />
    </Result.Loading>
  );

  const renderResults = (searchQuery: string) => {
    if (!isLocalLoading && bySearch.length === 0 && searchQuery.length > minLengthForSearch) {
      return (
        <Result.NotFound>
          <h2>Ops!</h2>
          <span>{t('error.notFound')}</span>
        </Result.NotFound>
      );
    }

    return bySearch.map((city, index) => (
      <Result.Item key={index} onClick={() => handleClickResultItem(city)}>
        <span>
          {`${city.description}${city.country !== null && city.country !== 'NULL' ? `, ${city.country}` : ''}`}
        </span>
      </Result.Item>
    ));
  };

  return (
    <ApplicationOverlayContent sidebar show={show} onClose={onClose}>
      <Header>
        <Header.Search
          ref={searchInputRef}
          value={searchQuery}
          placeholder={placeHolder}
          onChange={handleSearchInputChange}
          data-testid="city-search-input"
        />
        <Header.CloseIcon onClick={onClose} />
      </Header>
      <Result data-testid="city-search-result-list">
        {searchQuery.length <= minLengthForSearch && (
          <RecentSearches>{t('citiesSearch.recentSearches')}</RecentSearches>
        )}
        {isLocalLoading ? renderLoading() : renderResults(searchQuery)}
      </Result>
    </ApplicationOverlayContent>
  );
};

export default withTheme(CitiesSearch);
